import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Globe, Zap, Users, Award, TrendingUp, MapPin, Mail, Linkedin, Star } from 'lucide-react';
import SEO from './SEO';

interface AboutPageProps {
    onBack: () => void;
}

const AboutPage: React.FC<AboutPageProps> = ({ onBack }) => {

    const milestones = [
        { year: '2022', event: 'EasyTrafficBot UG founded in Wuppertal, Germany.' },
        { year: '2023', event: 'Launched Traffic Creator platform with the first Modus Engine version.' },
        { year: '2024', event: 'Reached 10,000+ active campaigns. Expanded to 150+ countries.' },
        { year: '2025', event: 'Launched v4 Modus Engine with GA4 native event simulation & AI scheduling.' },
    ];

    const values = [
        { icon: <Shield className="w-6 h-6 text-[#ff5a0a]" />, title: 'Transparency', desc: 'We are upfront about how our traffic works, what it does and what it doesn\'t do. No false promises.' },
        { icon: <Globe className="w-6 h-6 text-[#ff5a0a]" />, title: 'Global Reach', desc: 'Our residential IP network spans 195+ countries with city-level precision targeting.' },
        { icon: <Zap className="w-6 h-6 text-[#ff5a0a]" />, title: 'Speed First', desc: 'Campaigns go live within minutes, not days. We built our infrastructure for instant scale.' },
        { icon: <Users className="w-6 h-6 text-[#ff5a0a]" />, title: 'Customer Success', desc: 'Our support team responds within 4 hours, 7 days a week. Your campaign success is our mission.' },
    ];

    const siteUrl = 'https://traffic-creator.com/';
    const aboutUrl = `${siteUrl}about`;
    const organizationId = `${siteUrl}#organization`;

    const schema = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "AboutPage",
                "@id": `${aboutUrl}#webpage`,
                "url": aboutUrl,
                "name": "About Traffic Creator",
                "description": "Learn about Traffic Creator, the website traffic platform operated by EasyTrafficBot UG since 2022, its mission, values, and technology.",
                "about": { "@id": organizationId },
                "mainEntity": { "@id": organizationId }
            },
            {
                "@type": "Organization",
                "@id": organizationId,
                "name": "EasyTrafficBot UG",
                "alternateName": "Traffic Creator",
                "url": siteUrl,
                "logo": `${siteUrl}favicon.svg`,
                "foundingDate": "2022",
                "description": "EasyTrafficBot UG operates the Traffic Creator website traffic platform from Wuppertal, Germany.",
                "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "Arrenbergsche Höfe 6, Building 44",
                    "addressLocality": "Wuppertal",
                    "postalCode": "42117",
                    "addressCountry": "DE"
                },
                "founder": {
                    "@type": "Person",
                    "name": "Martin Freiwald",
                    "jobTitle": "Founder & Managing Director",
                    "sameAs": "https://www.linkedin.com/in/martinfreiwald"
                },
                "contactPoint": {
                    "@type": "ContactPoint",
                    "email": "support@traffic-creator.com",
                    "contactType": "customer support",
                    "availableLanguage": ["English", "German"]
                },
                "areaServed": "Worldwide",
                "sameAs": [
                    "https://www.crunchbase.com/organization/easytrafficbot",
                    "https://www.linkedin.com/company/traffic-creator"
                ]
            }
        ]
    };

    return (
        <div className="min-h-screen bg-white font-sans">
            <SEO
                title="About Traffic Creator | EasyTrafficBot UG – Web Traffic Experts Since 2022"
                description="Learn about Traffic Creator, the premium website traffic platform operated by EasyTrafficBot UG since 2022. Meet our founder Martin Freiwald and read about our mission, values, and technology."
                keywords="about traffic creator, easytrafficbot ug, martin freiwald, traffic generation company, wuppertal germany"
                schema={schema}
            />

            {/* Sticky Header */}
            <div className="border-b border-gray-100 bg-white sticky top-0 z-50 shadow-sm">
                <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
                    <button onClick={onBack} className="flex items-center gap-2 text-gray-400 hover:text-[#ff5a0a] transition-colors text-xs font-bold uppercase tracking-wider">
                        <ArrowLeft size={16} /> Back
                    </button>
                    <Link to="/" className="flex items-center gap-2">
                        <span className="text-xl font-black text-[#ff5a0a] tracking-tight">TRAFFIC</span>
                        <span className="text-[10px] font-bold bg-black text-white px-1.5 py-0.5 rounded-sm uppercase tracking-wide">CREATOR</span>
                    </Link>
                </div>
            </div>

            {/* Hero */}
            <section className="bg-gradient-to-b from-gray-50 to-white pt-24 pb-20 px-6 border-b border-gray-100">
                <div className="max-w-5xl mx-auto text-center">
                    <p className="text-xs font-black uppercase tracking-widest text-[#ff5a0a] mb-4">About Us</p>
                    <h1 className="text-5xl md:text-7xl font-black text-gray-900 uppercase tracking-tight leading-[0.9] mb-8">
                        Built for<br /><span className="text-[#ff5a0a]">Webmasters.</span>
                    </h1>
                    <p className="text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed">
                        Traffic Creator is operated by <strong className="text-gray-800">EasyTrafficBot UG</strong>, a registered German technology company. We have been helping webmasters and digital agencies boost their online visibility since 2022.
                    </p>
                </div>
            </section>

            {/* Stats Bar */}
            <section className="bg-black text-white py-12 px-6">
                <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                    {[
                        { value: '14,000+', label: 'Active Users' },
                        { value: '2.8B+', label: 'Visits Delivered' },
                        { value: '150+', label: 'Countries Covered' },
                        { value: '4 yrs', label: 'In Operation' },
                    ].map((s, i) => (
                        <div key={i}>
                            <div className="text-3xl font-black text-[#ff5a0a] mb-1">{s.value}</div>
                            <div className="text-xs font-bold uppercase tracking-widest text-gray-400">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Our Story */}
            <section className="py-24 px-6">
                <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
                    <div>
                        <p className="text-xs font-black uppercase tracking-widest text-[#ff5a0a] mb-4">Our Story</p>
                        <h2 className="text-3xl font-black text-gray-900 uppercase tracking-tight mb-6">Why We Built This</h2>
                        <div className="space-y-4 text-gray-600 leading-relaxed">
                            <p>In 2022, Martin Freiwald was a freelance SEO consultant watching his clients' websites disappear into the depths of Google's index — not because their content was bad, but because low traffic told search engines nobody cared.</p>
                            <p>The existing tools were either blatant bot farms that triggered instant penalties, or expensive ad platforms with no guarantee of results. There was a clear gap: a service that could simulate <em>real, human-like traffic behavior</em> at scale.</p>
                            <p>Traffic Creator was built to fill that gap. We developed the <strong className="text-gray-900">Modus Engine</strong> — a proprietary traffic routing system that uses residential IPs, real browser fingerprints, and intelligent behavior simulation to deliver authentic-looking visits that actually move the SEO needle.</p>
                        </div>
                    </div>
                    <div className="space-y-6">
                        <p className="text-xs font-black uppercase tracking-widest text-[#ff5a0a] mb-4">Company Milestones</p>
                        {milestones.map((m, i) => (
                            <div key={i} className="flex gap-6 items-start">
                                <div className="min-w-[56px] text-right">
                                    <span className="text-sm font-black text-[#ff5a0a]">{m.year}</span>
                                </div>
                                <div className="flex-1 border-l-2 border-gray-200 pl-6 pb-4">
                                    <p className="text-sm text-gray-700 font-medium">{m.event}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Founder Section */}
            <section className="py-24 px-6 bg-gray-50 border-y border-gray-100">
                <div className="max-w-5xl mx-auto">
                    <p className="text-xs font-black uppercase tracking-widest text-[#ff5a0a] mb-4 text-center">Leadership</p>
                    <h2 className="text-3xl font-black text-gray-900 uppercase tracking-tight mb-16 text-center">Meet the Founder</h2>
                    <div className="bg-white border border-gray-200 shadow-sm p-10 flex flex-col md:flex-row gap-10 items-start rounded-sm max-w-3xl mx-auto">
                        <div className="w-24 h-24 rounded-sm bg-gradient-to-br from-[#ff5a0a] to-orange-400 flex items-center justify-center text-white text-4xl font-black flex-shrink-0">
                            MF
                        </div>
                        <div>
                            <h3 className="text-2xl font-black text-gray-900 uppercase tracking-tight mb-1">Martin Freiwald</h3>
                            <p className="text-sm font-bold text-[#ff5a0a] uppercase tracking-widest mb-4">Founder & Managing Director · EasyTrafficBot UG</p>
                            <p className="text-gray-600 leading-relaxed mb-4">
                                Martin has 8+ years of experience in digital marketing, SEO, and web automation. After working with agencies across Europe, he founded EasyTrafficBot UG in Wuppertal, Germany, to build traffic generation tooling that is both effective and legally sound.
                            </p>
                            <div className="flex flex-wrap gap-3 text-xs font-bold text-gray-500">
                                <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-sm">
                                    <MapPin size={12} className="text-[#ff5a0a]" /> Wuppertal, Germany
                                </div>
                                <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-sm">
                                    <Mail size={12} className="text-[#ff5a0a]" />
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: '<!--email_off--><a href="mailto:support@traffic-creator.com" class="hover:text-[#ff5a0a] transition-colors">support@traffic-creator.com</a><!--/email_off-->',
                                        }}
                                    />
                                </div>
                                <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-sm">
                                    <Award size={12} className="text-[#ff5a0a]" /> Registered: Amtsgericht Wuppertal, HRB 30863
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="py-24 px-6">
                <div className="max-w-5xl mx-auto">
                    <p className="text-xs font-black uppercase tracking-widest text-[#ff5a0a] mb-4 text-center">What We Stand For</p>
                    <h2 className="text-3xl font-black text-gray-900 uppercase tracking-tight mb-16 text-center">Our Core Values</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {values.map((v, i) => (
                            <div key={i} className="border border-gray-200 p-8 hover:border-[#ff5a0a] hover:shadow-lg transition-all group rounded-sm">
                                <div className="w-12 h-12 bg-orange-50 rounded-sm flex items-center justify-center mb-5 group-hover:bg-[#ff5a0a] group-hover:text-white transition-colors">
                                    {v.icon}
                                </div>
                                <h3 className="font-black text-gray-900 uppercase tracking-wide mb-2">{v.title}</h3>
                                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            
            {/* Testimonials */}
            <section className="bg-gradient-to-b from-white to-gray-50 py-24 px-6">
                <div className="max-w-6xl mx-auto">
                    <p className="text-xs font-black uppercase tracking-widest text-[#ff5a0a] mb-4 text-center">Wall of Love</p>
                    <h2 className="text-3xl md:text-5xl font-black text-gray-900 uppercase tracking-tight mb-16 leading-[0.9] text-center">
                        Trusted by <span className="text-[#ff5a0a]">Webmasters.</span>
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Testimonial 1 */}
                        <div className="bg-white border border-gray-100 p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                            <div className="flex gap-1 text-[#ff5a0a] mb-6">
                                <Star fill="currentColor" size={16} />
                                <Star fill="currentColor" size={16} />
                                <Star fill="currentColor" size={16} />
                                <Star fill="currentColor" size={16} />
                                <Star fill="currentColor" size={16} />
                            </div>
                            <p className="text-gray-700 leading-relaxed mb-6">
                                "The Modus Engine is exactly what we needed. We ran a 30-day campaign for a client struggling with high bounce rates, and not only did the metrics improve, but we saw a noticeable lift in organic rankings across their secondary keywords."
                            </p>
                            <div className="flex items-center gap-4">
                                <img src={`https://api.dicebear.com/7.x/notionists/svg?seed=Marcus`} alt="Marcus T." className="w-12 h-12 rounded-full border border-gray-100 bg-gray-50" />
                                <div>
                                    <div className="font-black text-gray-900 text-sm uppercase">Marcus T.</div>
                                    <div className="text-xs font-bold tracking-widest text-[#ff5a0a]">SEO Agency Owner</div>
                                </div>
                            </div>
                        </div>

                        {/* Testimonial 2 */}
                        <div className="bg-white border border-gray-100 p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                            <div className="flex gap-1 text-[#ff5a0a] mb-6">
                                <Star fill="currentColor" size={16} />
                                <Star fill="currentColor" size={16} />
                                <Star fill="currentColor" size={16} />
                                <Star fill="currentColor" size={16} />
                                <Star fill="currentColor" size={16} />
                            </div>
                            <p className="text-gray-700 leading-relaxed mb-6">
                                "Transparency was a big factor for us. We've been burned by other traffic providers who promised 'real humans' but delivered obvious bot traffic. Traffic Creator's residential IP network produces traffic that looks perfectly natural in GA4."
                            </p>
                            <div className="flex items-center gap-4">
                                <img src={`https://api.dicebear.com/7.x/notionists/svg?seed=Elena`} alt="Elena R." className="w-12 h-12 rounded-full border border-gray-100 bg-gray-50" />
                                <div>
                                    <div className="font-black text-gray-900 text-sm uppercase">Elena R.</div>
                                    <div className="text-xs font-bold tracking-widest text-[#ff5a0a]">Digital Marketing Dir.</div>
                                </div>
                            </div>
                        </div>

                        {/* Testimonial 3 */}
                        <div className="bg-white border border-gray-100 p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                            <div className="flex gap-1 text-[#ff5a0a] mb-6">
                                <Star fill="currentColor" size={16} />
                                <Star fill="currentColor" size={16} />
                                <Star fill="currentColor" size={16} />
                                <Star fill="currentColor" size={16} />
                                <Star fill="currentColor" size={16} />
                            </div>
                            <p className="text-gray-700 leading-relaxed mb-6">
                                "Setup took literally 3 minutes. The ability to specify the exact behavior parameters - bounce rate, time on site, and even nested pageviews - gives us complete control. It's an indispensable tool in our growth stack."
                            </p>
                            <div className="flex items-center gap-4">
                                <img src={`https://api.dicebear.com/7.x/notionists/svg?seed=davidT`} alt="David S." className="w-12 h-12 rounded-full border border-gray-100 bg-gray-50" />
                                <div>
                                    <div className="font-black text-gray-900 text-sm uppercase">David S.</div>
                                    <div className="text-xs font-bold tracking-widest text-[#ff5a0a]">E-commerce Founder</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 px-6 bg-black text-white text-center">
                <div className="max-w-2xl mx-auto">
                    <TrendingUp className="mx-auto mb-6 text-[#ff5a0a]" size={48} />
                    <h2 className="text-4xl font-black uppercase tracking-tight mb-4">Ready to Grow?</h2>
                    <p className="text-gray-400 mb-10 text-lg">Join 14,000+ webmasters who trust Traffic Creator to build their online authority.</p>
                    <Link to="/signup" className="inline-flex bg-[#ff5a0a] text-white px-10 py-5 text-sm font-black uppercase tracking-widest hover:bg-white hover:text-black transition-all shadow-[0_10px_40px_rgba(255,90,10,0.4)]">
                        Start Your Campaign
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-black border-t border-gray-800 text-white py-10 px-6">
                <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-600">
                    <p>© {new Date().getFullYear()} EasyTrafficBot UG (haftungsbeschränkt) · Amtsgericht Wuppertal, HRB 30863</p>
                    <div className="flex gap-6 font-bold uppercase tracking-widest">
                        <Link to="/legal/impressum" className="hover:text-[#ff5a0a] transition-colors">Impressum</Link>
                        <Link to="/legal/privacy" className="hover:text-[#ff5a0a] transition-colors">Privacy</Link>
                        <Link to="/legal/agb" className="hover:text-[#ff5a0a] transition-colors">AGB</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default AboutPage;
